import { useState } from "react";
import { motion } from "framer-motion";
import { Bus, Calendar, Clock, MapPin, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { TRIPS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function seatColor(seats) {
  if (seats <= 5) return "text-red-400 border-red-400/40 bg-red-400/10";
  if (seats <= 15) return "text-gold border-gold/40 bg-gold/10";
  return "text-emerald-400 border-emerald-400/40 bg-emerald-400/10";
}

export default function Stadio() {
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ nome: "", telefono: "", posti: 1 });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/booking`, {
        nome: form.nome,
        telefono: form.telefono,
        posti: Number(form.posti),
        trip_id: active.id,
        trip_label: active.opponent,
      });
      toast.success("Prenotazione inviata!", { description: `Ti ricontatteremo per ${active.opponent}.` });
      setActive(null);
      setForm({ nome: "", telefono: "", posti: 1 });
    } catch {
      toast.error("Errore durante la prenotazione. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="stadio" data-testid="stadium-trips-section" className="relative bg-[#0B0E14] py-16 md:py-24 overflow-hidden">
      <div
        className="absolute inset-0 opacity-20 bg-cover bg-center"
        style={{ backgroundImage: "url(https://images.pexels.com/photos/10785346/pexels-photo-10785346.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)" }}
      />
      <div className="absolute inset-0 bg-[#0B0E14]/85" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-widest text-gold">Vieni allo Stadio</span>
          <h2 className="mt-3 font-display font-extrabold uppercase tracking-tight text-3xl md:text-4xl text-white">
            Vieni a San Siro con noi
          </h2>
          <p className="mt-4 text-white/70 font-medium">
            Organizziamo il pullman per ogni grande partita. Prenota il tuo posto: i posti sono limitati!
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {TRIPS.map((t, i) => (
            <motion.div
              key={t.id}
              data-testid="stadium-trip-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="flex flex-col rounded-2xl border border-[#1E2638] bg-[#131822] p-6 hover:border-primary/50 transition-colors"
            >
              <span className="inline-flex w-fit rounded-full bg-primary/15 text-primary px-3 py-1 text-xs font-bold uppercase tracking-widest">
                {t.competition}
              </span>
              <h3 className="mt-4 font-display font-bold text-2xl text-white leading-tight">{t.opponent}</h3>

              <div className="mt-4 space-y-2 text-sm text-white/70 font-medium">
                <p className="flex items-center gap-2"><Calendar size={15} className="text-gold" /> {t.date}</p>
                <p className="flex items-center gap-2"><Clock size={15} className="text-gold" /> Ore {t.time}</p>
                <p className="flex items-center gap-2"><MapPin size={15} className="text-gold" /> {t.departure}</p>
              </div>

              <div className={`mt-4 inline-flex w-fit items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-bold ${seatColor(t.seats)}`}>
                {t.seats} posti disponibili / {t.total}
              </div>

              <button
                data-testid="book-bus-trip-button"
                onClick={() => setActive(t)}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-3 font-bold uppercase tracking-wide text-[#0B0E14] hover:brightness-110 transition-all"
              >
                <Bus size={17} /> Prenota il posto
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {active && (
        <div
          data-testid="booking-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setActive(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-border bg-card p-6 md:p-8 shadow-2xl"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-primary">{active.competition}</p>
                <h3 className="font-display font-bold text-2xl mt-1">{active.opponent}</h3>
                <p className="text-sm text-muted-foreground">{active.date} • Ore {active.time}</p>
              </div>
              <button data-testid="booking-modal-close" onClick={() => setActive(null)} className="p-1 rounded-full hover:bg-secondary">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-sm font-semibold">Nome e Cognome</label>
                <input
                  data-testid="booking-input-name"
                  required
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-input bg-background px-4 py-2.5 outline-none focus:border-primary"
                  placeholder="Mario Rossi"
                />
              </div>
              <div>
                <label className="text-sm font-semibold">Telefono</label>
                <input
                  data-testid="booking-input-phone"
                  required
                  value={form.telefono}
                  onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-input bg-background px-4 py-2.5 outline-none focus:border-primary"
                  placeholder="+39 320 000 0000"
                />
              </div>
              <div>
                <label className="text-sm font-semibold">Numero di posti</label>
                <input
                  data-testid="booking-input-seats"
                  type="number"
                  min="1"
                  max="10"
                  required
                  value={form.posti}
                  onChange={(e) => setForm({ ...form, posti: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-input bg-background px-4 py-2.5 outline-none focus:border-primary"
                />
              </div>
              <button
                data-testid="booking-submit-button"
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-primary px-5 py-3 font-bold uppercase tracking-wide text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                {loading ? "Invio..." : "Conferma prenotazione"}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </section>
  );
}
